/**
 * Telas de autenticação.
 *
 * Um único componente com etapas, em vez de rotas separadas, porque o fluxo é linear e o usuário
 * precisa carregar o e-mail digitado entre uma etapa e outra — no legado isso era feito com um
 * `<input type="hidden">` e cinco blocos alternados por classe CSS.
 */

import { type ReactNode, type SyntheticEvent, useState } from 'react';
import { useNavigate } from 'react-router';

import { useAutenticacao } from '../autenticacao/contexto.tsx';
import { DOMINIO_CORPORATIVO } from '../autenticacao/configuracao.ts';
import {
  cadastrar,
  concluirRecuperacao,
  confirmarCadastro,
  definirNovaSenha,
  ehEmailCorporativo,
  entrar,
  ErroAutenticacao,
  iniciarRecuperacao,
  reenviarCodigo,
} from '../autenticacao/servico.ts';

type Etapa =
  | 'entrar'
  | 'cadastrar'
  | 'confirmar_cadastro'
  | 'nova_senha'
  | 'recuperar'
  | 'confirmar_recuperacao';

const TITULOS: Readonly<Record<Etapa, string>> = {
  entrar: 'Acesso ao painel',
  cadastrar: 'Criar sua conta',
  confirmar_cadastro: 'Confirme seu cadastro',
  nova_senha: 'Defina uma nova senha',
  recuperar: 'Recuperar senha',
  confirmar_recuperacao: 'Redefinir senha',
};

function Campo({
  id,
  rotulo,
  tipo,
  valor,
  onChange,
  autoComplete,
  dica,
}: {
  readonly id: string;
  readonly rotulo: string;
  readonly tipo: 'email' | 'password' | 'text';
  readonly valor: string;
  readonly onChange: (v: string) => void;
  readonly autoComplete?: string;
  readonly dica?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-brand-blue">
        {rotulo}
      </label>
      <input
        id={id}
        type={tipo}
        value={valor}
        required
        autoComplete={autoComplete}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm outline-none transition-colors focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/30"
      />
      {dica !== undefined && <p className="mt-1 text-xs text-gray-500">{dica}</p>}
    </div>
  );
}

function CampoSenha({
  valor,
  onChange,
  rotulo,
  id,
  autoComplete,
  placeholder,
}: {
  readonly valor: string;
  readonly onChange: (v: string) => void;
  readonly rotulo: string;
  readonly id: string;
  readonly autoComplete?: string;
  readonly placeholder?: string;
}) {
  const [visivel, setVisivel] = useState(false);

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-brand-blue">
        {rotulo}
      </label>
      <div className="relative mt-1">
        <input
          id={id}
          type={visivel ? 'text' : 'password'}
          value={valor}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          autoComplete={autoComplete}
          placeholder={placeholder}
          className="w-full rounded-xl border border-gray-200 px-4 py-3 pr-12 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
        />
        <button
          type="button"
          onClick={() => {
            setVisivel((v) => !v);
          }}
          aria-label={visivel ? 'Ocultar senha' : 'Mostrar senha'}
          title={visivel ? 'Ocultar senha' : 'Mostrar senha'}
          className="absolute inset-y-0 right-0 flex items-center px-4 text-gray-500 hover:text-brand-blue"
        >
          {visivel ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.8}
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.8}
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
              />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

function Botao({ children, ocupado }: { readonly children: ReactNode; readonly ocupado: boolean }) {
  return (
    <button
      type="submit"
      disabled={ocupado}
      className="flex w-full justify-center rounded-lg bg-brand-accent px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-gray-300"
    >
      {ocupado ? 'Aguarde...' : children}
    </button>
  );
}

function Link({
  onClick,
  children,
}: {
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="font-semibold text-brand-accent transition-colors hover:text-orange-600"
    >
      {children}
    </button>
  );
}

export function TelaAutenticacao() {
  const { recarregar } = useAutenticacao();
  const navegar = useNavigate();

  const [etapa, setEtapa] = useState<Etapa>('entrar');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [senhaNova, setSenhaNova] = useState('');
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  function irPara(proxima: Etapa, mensagem?: string) {
    setEtapa(proxima);
    setErro(null);
    setAviso(mensagem ?? null);
  }

  async function comTratamento(acao: () => Promise<void>) {
    setOcupado(true);
    setErro(null);
    try {
      await acao();
    } catch (e) {
      setErro(e instanceof ErroAutenticacao ? e.message : 'Algo deu errado. Tente novamente.');
    } finally {
      setOcupado(false);
    }
  }

  async function concluirLogin() {
    await recarregar();
    // No react-router 8 a navegação é assíncrona: sem o await, a promessa fica solta.
    await navegar('/', { replace: true });
  }

  function aoEnviar(e: SyntheticEvent) {
    e.preventDefault();

    void comTratamento(async () => {
      switch (etapa) {
        case 'entrar': {
          const r = await entrar(email, senha);
          if (r.tipo === 'autenticado') return concluirLogin();
          if (r.tipo === 'nova_senha_exigida') {
            irPara(
              'nova_senha',
              'Este é o seu primeiro acesso. Defina uma senha própria para continuar.',
            );
            return;
          }
          irPara('confirmar_cadastro', 'Enviamos um novo código de verificação para o seu e-mail.');
          return;
        }

        case 'nova_senha':
          await definirNovaSenha(senhaNova);
          return concluirLogin();

        case 'cadastrar':
          await cadastrar(email, senha);
          irPara('confirmar_cadastro', 'Enviamos um código de verificação para o seu e-mail.');
          return;

        case 'confirmar_cadastro':
          await confirmarCadastro(email, codigo);
          irPara(
            'entrar',
            'Cadastro confirmado. Um administrador precisa liberar seu perfil de acesso antes do primeiro uso.',
          );
          return;

        case 'recuperar':
          await iniciarRecuperacao(email);
          irPara('confirmar_recuperacao', 'Enviamos um código de redefinição para o seu e-mail.');
          return;

        case 'confirmar_recuperacao':
          await concluirRecuperacao(email, codigo, senhaNova);
          irPara('entrar', 'Senha redefinida. Você já pode entrar com a senha nova.');
          return;
      }
    });
  }

  const dominioInvalido =
    etapa === 'cadastrar' && email.trim() !== '' && !ehEmailCorporativo(email);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <img
            src="https://emaster.cloud/wp-content/uploads/sites/10/2025/08/cropped-Logos-eMaster9.png"
            alt="eMaster Cloud"
            className="mx-auto h-10 w-auto"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <h1 className="mt-6 text-2xl font-bold tracking-tight text-brand-blue">
            {TITULOS[etapa]}
          </h1>
        </div>

        {aviso !== null && (
          <p className="mb-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-brand-blue" role="status">
            {aviso}
          </p>
        )}

        {erro !== null && (
          <p className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            {erro}
          </p>
        )}

        <form onSubmit={aoEnviar} className="space-y-5" noValidate>
          {(etapa === 'entrar' ||
            etapa === 'cadastrar' ||
            etapa === 'recuperar' ||
            etapa === 'confirmar_cadastro' ||
            etapa === 'confirmar_recuperacao') && (
            <Campo
              id="email"
              rotulo="E-mail"
              tipo="email"
              valor={email}
              onChange={setEmail}
              autoComplete="username"
              // Espalhamento condicional, e não `dica={... : undefined}`: com
              // `exactOptionalPropertyTypes`, passar `undefined` a uma prop declarada
              // `dica?: string` é erro de tipo, porque opcional significa ausente, e não
              // presente-e-indefinido.
              {...(etapa === 'cadastrar' ? { dica: `Restrito a ${DOMINIO_CORPORATIVO}` } : {})}
            />
          )}

          {(etapa === 'entrar' || etapa === 'cadastrar') && (
            <CampoSenha
              id="senha"
              rotulo="Senha"
              valor={senha}
              onChange={setSenha}
              autoComplete={etapa === 'entrar' ? 'current-password' : 'new-password'}
            />
          )}

          {(etapa === 'confirmar_cadastro' || etapa === 'confirmar_recuperacao') && (
            <Campo
              id="codigo"
              rotulo="Código de verificação"
              tipo="text"
              valor={codigo}
              onChange={setCodigo}
              autoComplete="one-time-code"
            />
          )}

          {(etapa === 'nova_senha' || etapa === 'confirmar_recuperacao') && (
            <CampoSenha
              id="senha-nova"
              rotulo="Nova senha"
              valor={senhaNova}
              onChange={setSenhaNova}
              autoComplete="new-password"
            />
          )}

          {dominioInvalido && (
            <p className="text-xs text-red-600">
              O cadastro é restrito a e-mails {DOMINIO_CORPORATIVO}.
            </p>
          )}

          <Botao ocupado={ocupado}>
            {etapa === 'entrar' && 'Entrar'}
            {etapa === 'cadastrar' && 'Cadastrar'}
            {etapa === 'confirmar_cadastro' && 'Confirmar cadastro'}
            {etapa === 'nova_senha' && 'Definir senha'}
            {etapa === 'recuperar' && 'Enviar código'}
            {etapa === 'confirmar_recuperacao' && 'Redefinir senha'}
          </Botao>
        </form>

        <div className="mt-6 space-y-2 text-center text-sm text-gray-500">
          {etapa === 'entrar' && (
            <>
              <p>
                Não tem conta?{' '}
                <Link
                  onClick={() => {
                    irPara('cadastrar');
                  }}
                >
                  Cadastre-se
                </Link>
              </p>
              <p>
                <Link
                  onClick={() => {
                    irPara('recuperar');
                  }}
                >
                  Esqueceu a senha?
                </Link>
              </p>
            </>
          )}

          {(etapa === 'cadastrar' || etapa === 'recuperar') && (
            <p>
              <Link
                onClick={() => {
                  irPara('entrar');
                }}
              >
                Voltar para o login
              </Link>
            </p>
          )}

          {etapa === 'confirmar_cadastro' && (
            <>
              <p>
                Não recebeu?{' '}
                <Link
                  onClick={() => {
                    void comTratamento(async () => {
                      await reenviarCodigo(email);
                      setAviso('Código reenviado.');
                    });
                  }}
                >
                  Reenviar código
                </Link>
              </p>
              <p>
                <Link
                  onClick={() => {
                    irPara('entrar');
                  }}
                >
                  Voltar para o login
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
